import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useTripData, TripData, useMinimumPrice } from '../data/tripData';
import { useLanguage } from '../hooks/useLanguage';
import { getDestinationsForTrip, getActivitiesForTrip } from '../data/relationships';
import { RelatedDestinations } from '../components/RelatedDestinations';
import { RelatedActivities } from '../components/RelatedActivities';
import { BookingForm } from '../components/BookingForm';
import TravelItinerary from '../components/TravelItinerary';
import ItineraryNavigationDots from '../components/ItineraryNavigationDots';
import { useAuth } from '../contexts/AuthContext';
import { bookingsApi, tripsApi } from '../services/api';
import { urlToDatabaseIdMap, getDatabaseName } from '../data/tripData';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatDate } from '../utils/dateFormatter';

type TabType = 'overview' | 'itinerary' | 'faqs' | 'book' | 'related';

export default function TripDetailsPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousTab, setPreviousTab] = useState<TabType | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedItinerary, setExpandedItinerary] = useState<number | null>(0);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen) {
        const target = event.target as Element;
        if (!target.closest('.menu-container')) {
          setIsMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);
  
  // Handle tab change with transition
  const handleTabChange = (newTab: TabType) => {
    if (newTab === activeTab || isTransitioning) return;
    
    setPreviousTab(activeTab);
    setIsTransitioning(true);
    setIsMenuOpen(false); // Close menu when changing tab
    
    // Clear FAQ search when switching away from FAQs tab
    if (activeTab === 'faqs' && newTab !== 'faqs') {
      setFaqSearchTerm('');
    }
    
    // Wait for fade-out to complete before changing tab
    setTimeout(() => {
      setActiveTab(newTab);
      // Wait for fade-in to complete
      setTimeout(() => {
        setIsTransitioning(false);
        setPreviousTab(null);
      }, 500);
    }, 300);
  };
  const [expandedFaqs, setExpandedFaqs] = useState<number | null>(null);
  const [expandAllItinerary, setExpandAllItinerary] = useState(false);
  const [expandAllFaqs, setExpandAllFaqs] = useState(false);
  const [faqSearchTerm, setFaqSearchTerm] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [hasAvailableDates, setHasAvailableDates] = useState<boolean | null>(null);
  const [userBooking, setUserBooking] = useState<any>(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [loadingTripDates, setLoadingTripDates] = useState(false);
  const [tripDatesLoaded, setTripDatesLoaded] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [tripWithPricing, setTripWithPricing] = useState<any>(null);
  const [selectedDatePricing, setSelectedDatePricing] = useState<any>(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMobileMenuOpen && !target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Get trip data based on tripId
  const tripData = tripId ? useTripData(tripId) : null;
  const { price: minimumPrice, loading: priceLoading } = useMinimumPrice(tripId || '');
  const relatedDestinations = tripId ? getDestinationsForTrip(tripId) : [];
  const relatedActivities = tripId ? getActivitiesForTrip(tripId) : [];

  // Set page title based on trip data
  usePageTitle(tripData ? `${tripData.title} - La Dolce Vita` : undefined);

  // Load trip dates and check user booking
  useEffect(() => {
    const loadTripData = async () => {
      if (!tripData || tripDatesLoaded) return;
      
      // Clear any existing timeout
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      
      // Add a small delay to debounce multiple calls
      const timeout = setTimeout(async () => {
        try {
          setLoadingTripDates(true);
          setLoadingBooking(true);
          
          // Load trip data with pricing information (force canonical DB name)
          const tripResponse = await tripsApi.getTripsByName(getDatabaseName(tripData.title));
          if (tripResponse.success) {
            setTripWithPricing(tripResponse.trip);
            // Check if trip has available dates
            const hasDates = tripResponse.trip && tripResponse.trip.availableDates && tripResponse.trip.availableDates.length > 0;
            setHasAvailableDates(hasDates);
          } else {
            // If API call fails (404 or other error), no dates available
            setHasAvailableDates(false);
          }
          setTripDatesLoaded(true);
          
          // Check user booking
          if (user) {
            const bookingResponse = await bookingsApi.getUserBookings();
            if (bookingResponse.success) {
              const databaseId = tripId ? urlToDatabaseIdMap[tripId] : null;
              const booking = bookingResponse.bookings.find((b: any) => b.trip.id === databaseId);
              setUserBooking(booking || null);
            }
          }
        } catch (error) {
          console.error('Error loading trip data:', error);
          setUserBooking(null);
          // If API call fails (404 or other error), no dates available
          setHasAvailableDates(false);
        } finally {
          setLoadingTripDates(false);
          setLoadingBooking(false);
        }
      }, 100); // 100ms debounce
      
      setLoadingTimeout(timeout);
    };

    loadTripData();
    
    // Cleanup timeout on unmount
    return () => {
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, [user, tripId, tripData?.title, tripDatesLoaded]);

  // Determine breadcrumb category based on trip data
  const getBreadcrumbCategory = () => {
    if (!tripData) return t('tripDetails.breadcrumbTrips');
    return t('tripDetails.breadcrumbTrips');
  };

  // Handle booking button click - show booking form
  const handleBookingClick = () => {
    setShowBookingForm(true);
  };

  // If trip not found, show error page
  if (!tripData) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('tripDetails.tripNotFound')}</h1>
            <p className="text-gray-600 mb-8">{t('tripDetails.tripNotFoundDescription')}</p>
            <Link 
              to="/" 
              className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('tripDetails.backToHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const toggleItinerary = (day: number) => {
    setExpandedItinerary(expandedItinerary === day ? null : day);
  };

  const toggleFaq = (index: number) => {
    setExpandedFaqs(expandedFaqs === index ? null : index);
  };

  const toggleAllItinerary = () => {
    setExpandAllItinerary(!expandAllItinerary);
    if (expandAllItinerary) {
      // Se tutti sono espanse, li comprimo tutti
      setExpandedItinerary(null);
    } else {
      // Se sono compressi, espando il primo
      setExpandedItinerary(1);
    }
  };

  const toggleAllFaqs = () => {
    setExpandAllFaqs(!expandAllFaqs);
    if (expandAllFaqs) {
      // Se tutte sono espanse, le comprimo tutte
      setExpandedFaqs(null);
    } else {
      // Se sono compresse, espando la prima
      setExpandedFaqs(0);
    }
  };

  const tabs: { id: TabType; label: string }[] = [
    { id: 'overview', label: t('tripDetails.overview') },
    { id: 'itinerary', label: t('tripDetails.itinerary') },
    { id: 'book', label: t('tripDetails.bookThisTrip') },
    { id: 'related', label: t('common.related') },
    { id: 'faqs', label: t('tripDetails.faqs') }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div 
        className="text-gray-700 text-lg prose prose-lg max-w-none pt-4 md:pt-0"
        dangerouslySetInnerHTML={{ __html: tripData.overviewDescription || tripData.description }}
      />
    </div>
  );

  const renderItinerary = () => (
    <div className="space-y-6">
      <TravelItinerary tripData={tripData} />
    </div>
  );

  const renderFaqs = () => {
    // Filter FAQs based on search term
    const filteredFaqs = tripData.faqs.filter((faq: any) => {
      if (!faqSearchTerm.trim()) return true;
      const searchLower = faqSearchTerm.toLowerCase();
      return (
        faq.question.toLowerCase().includes(searchLower) ||
        faq.answer.toLowerCase().includes(searchLower)
      );
    });

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center mt-4">
          <h2 className="text-3xl font-bold text-gray-900">{t('tripDetails.faqs')}</h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{t('tripDetails.expandAll')}</span>
            <button
              onClick={toggleAllFaqs}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                expandAllFaqs ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  expandAllFaqs ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Search Form */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder={t('tripDetails.searchFaqs') || 'Cerca nelle domande e risposte...'}
              value={faqSearchTerm}
              onChange={(e) => setFaqSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {faqSearchTerm && (
              <button
                onClick={() => setFaqSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {faqSearchTerm && (
            <div className="mt-2 text-sm text-gray-600">
              {filteredFaqs.length === 0 ? (
                <span className="text-red-600">
                  {t('tripDetails.noSearchResults').replace('{term}', faqSearchTerm)}
                </span>
              ) : (
                <span>
                  {t('tripDetails.searchResults')
                    .replace('{count}', filteredFaqs.length.toString())
                    .replace('{term}', faqSearchTerm)}
                </span>
              )}
            </div>
          )}
        </div>
        
        <div className="space-y-4">
          {filteredFaqs.map((faq: any, index: number) => {
            // Find the original index for proper expansion handling
            const originalIndex = tripData.faqs.findIndex(originalFaq => originalFaq === faq);
            
            return (
              <div key={originalIndex} className="bg-gray-50 rounded-lg">
                <button
                  onClick={() => toggleFaq(originalIndex)}
                  className="flex items-center justify-between w-full p-4 text-left hover:bg-gray-100 transition-colors rounded-lg"
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      (expandedFaqs === originalIndex || expandAllFaqs) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {(expandedFaqs === originalIndex || expandAllFaqs) && (
                  <div className="px-4 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  const renderBookingSection = () => {
    return (
      <div className="space-y-6">
        {/* Show booking title and description only when dates are available */}
        {hasAvailableDates !== false && (
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t('payment.bookThisTrip')}
            </h3>
            <p className="text-gray-700 mb-4">
              {t('payment.completeBooking')}
            </p>
          </div>
        )}

        {/* Minimum Price Information */}
        {!priceLoading && minimumPrice && hasAvailableDates !== false && (
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-lg relative z-10">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                {t('common.startingFrom')}
              </h4>
              <div className="text-3xl font-bold text-green-600 mb-2">
                {minimumPrice}
              </div>
              <p className="text-sm text-gray-600">
                {t('payment.pricePerPerson')}
              </p>
            </div>
          </div>
        )}

        {/* Available Dates Section */}
        {loadingTripDates ? (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Date Disponibili</h4>
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          </div>
        ) : null}

        <div className="text-center">
          {!user ? (
            <div>
              <p className="text-gray-600 mb-4">{t('tripDetails.doYouWantToBook')}</p>
              <Link
                to="/login"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg"
              >
                {t('common.login')}
              </Link>
            </div>
          ) : loadingBooking ? (
            <div className="animate-pulse">
              <div className="h-12 bg-gray-200 rounded-lg mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
            </div>
          ) : userBooking ? (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium">Hai già una prenotazione per questo viaggio</p>
                <p className="text-green-600 text-sm mt-1">
                  {userBooking.numberOfQuotes} {userBooking.numberOfQuotes === 1 ? 'partecipante' : 'partecipanti'}
                </p>
              </div>
              <Link
                to="/account"
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg"
              >
                Vai al tuo account
              </Link>
            </div>
          ) : (
            <div>
              {hasAvailableDates === false ? (
                // Show coming soon message when no dates available
                <div className="text-center">
                  <div className="mb-4">
                    <svg className="w-16 h-16 mx-auto text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {t('booking.datesComingSoon')}
                  </h3>
                  <p className="text-gray-600 text-lg mb-4">
                    {t('booking.followSocialForUpdates')}
                  </p>
                  
                  {/* Social Media Links */}
                  <div className="flex justify-center space-x-4 mb-4">
                    <a 
                      href="https://www.facebook.com/profile.php?id=61580521581922" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M22 12.06C22 6.48 17.52 2 11.94 2 6.48 2 2 6.48 2 12.06 2 17.08 5.66 21.21 10.44 22v-7.02H7.9v-2.92h2.54V9.41c0-2.5 1.49-3.88 3.78-3.88 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.89h2.77l-.44 2.92h-2.33V22C18.34 21.21 22 17.08 22 12.06z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://www.instagram.com/ladolcevitareis" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-pink-600 hover:text-pink-800 transition-colors"
                    >
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M7 2h10a5 5 0 015 5v10a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5zm0 2a3 3 0 00-3 3v10a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H7zm5 3.5A5.5 5.5 0 1112 18.5 5.5 5.5 0 0112 7.5zm0 2A3.5 3.5 0 1015.5 13 3.5 3.5 0 0012 9.5zM18 6.25a1.25 1.25 0 11-1.25 1.25A1.25 1.25 0 0118 6.25z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              ) : (
                // Show booking button when dates are available or still loading
                <div>
                  <button
                    onClick={handleBookingClick}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg"
                  >
                    {t('booking.startBooking')}
                  </button>
                  <p className="text-sm text-gray-600 mt-2">
                    {t('booking.startBookingDescription')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Show important info only when dates are available */}
        {hasAvailableDates !== false && (
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-3">{t('payment.importantInfo.title')}</h4>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>{t('payment.importantInfo.depositPercent')}</li>
              <li>{t('payment.importantInfo.balancePercent')}</li>
              <li>{t('payment.importantInfo.stripeSecure')}</li>
              <li>{t('payment.importantInfo.noCardStorage')}</li>
              <li>{t('payment.importantInfo.emailConfirmation')}</li>
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderRelated = () => (
    <div className="space-y-6">
      <RelatedDestinations 
        destinationIds={relatedDestinations} 
        title={t('common.includesDestinations')}
      />
      <RelatedActivities 
        activityIds={relatedActivities} 
        title={t('common.includesActivities')}
      />
      
      {/* Creative Commons Attribution */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <p className="text-left text-xs text-gray-500">
          <span>{t('landing.footer.creativeCommons')}</span>
          <a 
            href="/commons.wikimedia.info"
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600 underline"
          >
            {t('landing.footer.fullCredits')}
          </a>
        </p>
      </div>
    </div>
  );

  // If trip is not active, show coming soon message
  if (!tripData.active) {
    // Temporary hardcoded messages by language until translation system is fixed
    const getComingSoonMessage = () => {
      switch (language) {
        case 'it': return 'Il viaggio sarà presto disponibile, rimani sintonizzato ❤️';
        case 'fr': return 'Le voyage sera bientôt disponible, restez à l\'écoute ❤️';
        case 'es': return 'El viaje estará disponible pronto, mantente atento ❤️';
        case 'de': return 'Die Reise wird bald verfügbar sein, bleiben Sie dran ❤️';
        case 'nl': return 'De reis zal binnenkort beschikbaar zijn, blijf op de hoogte ❤️';
        case 'tr': return 'Gezi yakında mevcut olacak, bizi takip edin ❤️';
        default: return 'Trip will be soon available, stay tuned ❤️';
      }
    };
    
    const comingSoonMessage = getComingSoonMessage();
    
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        
        {/* Hero Section */}
        <div className="flex-1 relative h-96 bg-cover bg-center" style={{ backgroundImage: `url(${tripData.image})` }}>
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl font-bold mb-4">{tripData.title}</h1>
              <p className="text-xl mb-8">{tripData.subtitle}</p>
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-8 max-w-md mx-auto">
                <p className="text-2xl font-semibold text-white">
                  {comingSoonMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <div className="relative h-96 min-h-96 flex-shrink-0 bg-cover bg-center" style={{ backgroundImage: `url(${tripData.image})` }}>
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          <div className="text-white">
            <h1 className="text-4xl font-bold mb-4">{tripData.title}</h1>
            <p className="text-xl mb-6">{tripData.subtitle}</p>
            {/* Mobile Book Button with Price - positioned under text */}
            <div className="md:hidden">
              {!priceLoading && minimumPrice ? (
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-center">
                    <p className="text-sm text-white opacity-90 mb-1">
                      {t('common.startingFrom')}
                    </p>
                    <p className="text-2xl font-bold text-white mb-3">
                      {minimumPrice}
                    </p>
                    <p className="text-xs text-white opacity-80 mb-4">
                      {t('payment.pricePerPerson')}
                    </p>
                    <button
                      onClick={() => handleTabChange('book')}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 text-base shadow-lg w-full"
                    >
                      {t('payment.bookThisTrip')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => handleTabChange('book')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 text-base shadow-lg"
                >
                  {t('payment.bookThisTrip')}
                </button>
              )}
            </div>
          </div>
          {/* Desktop Book Button with Price - positioned on the right */}
          <div className="hidden md:block">
            {!priceLoading && minimumPrice ? (
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-6">
                <div className="text-center">
                  <p className="text-sm text-white opacity-90 mb-2">
                    {t('common.startingFrom')}
                  </p>
                  <p className="text-3xl font-bold text-white mb-2">
                    {minimumPrice}
                  </p>
                  <p className="text-sm text-white opacity-80 mb-6">
                    {t('payment.pricePerPerson')}
                  </p>
                  <button
                    onClick={() => handleTabChange('book')}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 w-full"
                  >
                    {t('payment.bookThisTrip')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => handleTabChange('book')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {t('payment.bookThisTrip')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-gray-500 hover:text-blue-600">{t('common.home')}</Link>
            <span className="text-gray-400">/</span>
            <Link 
              to="/#trips" 
              onClick={(e) => {
                e.preventDefault();
                // Naviga alla home e poi scrolla alla sezione trips
                window.location.href = '/#trips';
              }}
              className="text-gray-500 hover:text-blue-600 cursor-pointer"
            >
              {t('tripDetails.breadcrumbTrips')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {tripData.title}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 container mx-auto px-4 py-3 lg:py-8">
        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-3 lg:mb-8">
          <div className="border-b border-gray-200">
            {/* Mobile Menu Button */}
            <div className="lg:hidden px-3 py-2 mobile-menu-container menu-container relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-sm"
              >
                <span className="flex items-center">
                  <span className="mr-2">📋</span>
                  {tabs.find(tab => tab.id === activeTab)?.label}
                </span>
                <svg
                  className={`ml-2 h-5 w-5 transition-transform duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Mobile Menu Dropdown */}
              <div className={`absolute top-full left-0 right-0 mt-1 border border-gray-200 rounded-lg bg-white shadow-lg z-50 transition-all duration-500 ease-in-out ${
                isMobileMenuOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      handleTabChange(tab.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-medium transition-all duration-300 ease-in-out first:rounded-t-lg last:rounded-b-lg ${
                      activeTab === tab.id
                        ? 'bg-gray-100 text-gray-800'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-800'
                    }`}
                  >
                    <span className="flex items-center">
                      <span className="mr-2">
                        {tab.id === 'overview' && '📈'}
                        {tab.id === 'itinerary' && '🗺️'}
                        {tab.id === 'faqs' && '❓'}
                        {tab.id === 'book' && '📅'}
                        {tab.id === 'related' && '🔗'}
                      </span>
                      {tab.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop Expanded Menu Navigation */}
            <div className="hidden lg:block px-6">
              <nav className="flex space-x-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center px-6 py-4 text-sm font-medium transition-all duration-500 ease-in-out border-b-2 ${
                      activeTab === tab.id
                        ? 'text-gray-800 border-gray-800'
                        : 'text-gray-600 hover:text-gray-700 border-transparent hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-3 text-lg">
                      {tab.id === 'overview' && '📈'}
                      {tab.id === 'itinerary' && '🗺️'}
                      {tab.id === 'faqs' && '❓'}
                      {tab.id === 'book' && '📅'}
                      {tab.id === 'related' && '🔗'}
                    </span>
                    <span className="font-semibold">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
          
          <div className="p-3 lg:p-6 relative -mt-2 lg:mt-0">
            {/* Previous tab (fade-out) */}
            {previousTab && isTransitioning && (
              <div className="tab-content fade-out absolute inset-0 z-10">
                {previousTab === 'overview' && renderOverview()}
                {previousTab === 'itinerary' && renderItinerary()}
                {previousTab === 'faqs' && renderFaqs()}
                {previousTab === 'book' && renderBookingSection()}
                {previousTab === 'related' && renderRelated()}
              </div>
            )}
            
            {/* Current tab (fade-in) */}
            <div className={`tab-content ${activeTab === 'overview' ? 'active' : ''}`}>
              {activeTab === 'overview' && renderOverview()}
            </div>
            <div className={`tab-content ${activeTab === 'itinerary' ? 'active' : ''}`}>
              {activeTab === 'itinerary' && renderItinerary()}
            </div>
            <div className={`tab-content ${activeTab === 'faqs' ? 'active' : ''}`}>
              {activeTab === 'faqs' && renderFaqs()}
            </div>
            <div className={`tab-content ${activeTab === 'book' ? 'active' : ''}`}>
              {activeTab === 'book' && renderBookingSection()}
            </div>
            <div className={`tab-content ${activeTab === 'related' ? 'active' : ''}`}>
              {activeTab === 'related' && renderRelated()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Itinerary Navigation Dots - Only show when in itinerary tab */}
      {activeTab === 'itinerary' && (
        <ItineraryNavigationDots tripData={tripData} />
      )}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <BookingForm
          tripId={tripId || ''}
          tripName={tripData.title}
          totalPrice={minimumPrice ? parseInt(minimumPrice.replace(/[^0-9]/g, '')) : 0}
          tripWithDates={tripWithPricing ? {
            ...tripWithPricing,
            availableDates: tripWithPricing.availableDates
          } : null}
          onBookingSuccess={(bookingId) => {
            setShowBookingForm(false);
            console.log('Prenotazione creata con successo:', bookingId);
          }}
          onCancel={() => setShowBookingForm(false)}
        />
      )}
      
      <Footer />
    </div>
  );
} 